import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { AcknowledgmentField } from '../../../../../models/form/fields/acknowledgment';
import { BirthDateField } from '../../../../../models/form/fields/birthdate';
import { CheckboxField } from '../../../../../models/form/fields/checkbox';
import { DateField } from '../../../../../models/form/fields/date';
import { DateTimeField } from '../../../../../models/form/fields/datetime';
import { DocumentViewerField } from '../../../../../models/form/fields/documentViewer';
import { EmailField } from '../../../../../models/form/fields/email';
import { EmbeddedPhotoField } from '../../../../../models/form/fields/embeddedPhoto';
// import { HtmlField } from '../../../../../models/form/fields/html';
import { LongTextField } from '../../../../../models/form/fields/longText';
import { NumericField } from '../../../../../models/form/fields/numeric';
import { OptionListField } from '../../../../../models/form/fields/optionList';
import { RatingField } from '../../../../../models/form/fields/rating';
import { SelectField } from '../../../../../models/form/fields/select';
import { SignatureField } from '../../../../../models/form/fields/signature';
import { StaticTextField } from '../../../../../models/form/fields/staticText';
import { TextField } from '../../../../../models/form/fields/text';
import { TimeField } from '../../../../../models/form/fields/time';
import { UploadFileField } from '../../../../../models/form/fields/uploadFile';
import { ToggleField } from '../../../../../models/form/fields/toggle';
import { VideoField } from '../../../../../models/form/fields/video';
import { WeblinkField } from '../../../../../models/form/fields/weblink';
import { YesNoField } from '../../../../../models/form/fields/yesNo';
import { Accordion, AccordionHeader } from 'react-bootstrap';
import { PaymentOptionList } from '../../../../../models/form/fields/paymentOptionList';
import { PaymentChoiceList } from '../../../../../models/form/fields/paymentChoiceList';
import { addField, formAdminErrors, removeError } from '../../../../../slices/formadmin';
import { addErrorToast } from '../../../../../components/errors/toast/ToastUtils';
import { useIntl } from 'react-intl';
import { RootState } from '../../../../../store';
import { Products } from '../../../../../models/form/fields/products';

const toolbox = [
  {
    key: 'textList',
    label: 'toolbox.list.text',
    content: [
      { key: 'textField', type: TextField, class: 'mdi-form-textbox' },
      { key: 'longTextField', type: LongTextField, class: 'mdi-form-textarea' },
      {
        key: 'staticTextField',
        type: StaticTextField,
        class: 'mdi-format-color-text',
      },
      { key: 'emailField', type: EmailField, class: 'mdi-at' },
      { key: 'numericField', type: NumericField, class: 'mdi-numeric' },
    ],
  },
  {
    key: 'toggleList',
    label: 'toolbox.list.toggle',
    content: [
      {
        key: 'checkboxField',
        type: CheckboxField,
        class: 'mdi-checkbox-marked-outline',
      },
      { key: 'toggleField', label: 'toolbox.fields.toggle', type: ToggleField, class: 'mdi-toggle-switch-off-outline' },
      {
        key: 'acknowledgmentField',
        type: AcknowledgmentField,
        class: 'mdi-checkbox-marked-outline',
      },
    ],
  },
  {
    key: 'dateList',
    label: 'toolbox.list.date',
    content: [
      { key: 'dateField', type: DateField, class: 'mdi-calendar' },
      { key: 'dateTimeField', type: DateTimeField, class: 'mdi-calendar-clock' },
      { key: 'timeField', type: TimeField, class: 'mdi-clock-time-four-outline' },
      { key: 'birthdateField', type: BirthDateField, class: 'mdi-cake-variant' },
    ],
  },

  {
    key: 'selectionList',
    label: 'toolbox.list.selection',
    content: [
      { key: 'selectField', type: SelectField, class: 'mdi-view-list-outline' },
      {
        key: 'optionListField',
        type: OptionListField,
        class: 'mdi-format-list-bulleted',
      },
      { key: 'yesNoField', type: YesNoField, class: 'mdi-arrow-decision' },
    ],
  },
  {
    key: 'embeddedList',
    label: 'toolbox.list.embedded',
    content: [
      {
        key: 'embeddedPhotoField',
        type: EmbeddedPhotoField,
        class: 'mdi-image',
      },
      { key: 'videoField', type: VideoField, class: 'mdi-video' },
      { key: 'weblinkField', type: WeblinkField, class: 'mdi-link' },
      {
        key: 'documentViewerField',
        type: DocumentViewerField,
        class: 'mdi-file-document-multiple',
      },
      // { key: 'htmlField', type: HtmlField, class: 'mdi-language-html5' },
    ],
  },
  {
    key: 'miscellaneousList',
    label: 'toolbox.list.miscellaneous',
    content: [
      { key: 'ratingField', type: RatingField, class: 'mdi-star' },
      { key: 'fileUpload', type: UploadFileField, class: 'mdi-upload' },
      { key: 'signatureField', type: SignatureField, class: 'mdi-draw' },
    ],
  },
  {
    key: 'payment',
    label: 'toolbox.list.payment',
    content: [
      {
        key: 'paymentOptionField',
        label: 'toolbox.fields.paymentOptionList',
        type: PaymentOptionList,
        class: 'mdi-view-list-outline',
      },
      {
        key: 'paymentChoiceField',
        label: 'toolbox.fields.paymentChoiceList',
        type: PaymentChoiceList,
        class: 'mdi-view-list-outline',
      },
      {
        key: 'productsField',
        label: 'toolbox.fields.products',
        type: Products,
        class: 'mdi-cart-outline',
      },
    ],
  },
];

const Toolbox: FC = () => {
  //const [idx, setIdx] = useState<number[]>([]);
  const errors = useSelector((state: RootState) => state.formAdmin?.errors);
  const intl = useIntl();
  const dispatch = useDispatch();
  const localizations = useSelector((state: RootState) => state.formAdmin?.form?.localizations);

  useEffect(() => {
    let index = errors.indexOf(formAdminErrors.ADD_FIELD_IN_EMPTY_STEP);
    if (index != -1) {
      addErrorToast({ message: intl.formatMessage({ id: 'slices.formAdmin.fieldStepError' }) });
      dispatch(removeError(index));
    }
    index = errors.indexOf(formAdminErrors.ADD_FIELD_IN_EMPTY_SECTION);
    if (index != -1) {
      addErrorToast({ message: intl.formatMessage({ id: 'slices.formAdmin.fieldSectionError' }) });
      dispatch(removeError(index));
    }
  }, [errors]);

  return (
    <section className="toolbox">
      <ul className="input-fields">
        {toolbox.map((list) => {
          return (
            <Accordion key={list.key}>
              <Accordion.Item eventKey="0">
                <AccordionHeader>
                  <div className="w-100 d-inline-flex justify-content-between align-items-center">
                    <FormattedMessage id={list.label} />
                    {/* {idx.includes(index) ? (
                      <span className="chevron mdi mdi-menu-down"></span>
                    ) : (
                      <span className="chevron mdi mdi-menu-right"></span>
                    )} */}
                  </div>
                </AccordionHeader>
                <Accordion.Body>
                  <div>
                    {list.content.map((field, i) => {
                      const newField = new field.type(localizations);
                      return (
                        <li
                          className="input-field"
                          key={`${field.key}-${i}`}
                          onClick={() => dispatch(addField({ ...newField }))}
                        >
                          <span className="w-100 d-inline-flex justify-content-between  ">
                            <div>
                              <span className={`mdi ${field.class}`}></span>
                              <FormattedMessage id={'toolbox.fields.' + newField.type} />
                            </div>
                            <span className="fw-bold">+</span>
                          </span>
                        </li>
                      );
                    })}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          );
        })}
      </ul>
    </section>
  );
};

export default Toolbox;
